import { useEffect, useRef, useState } from "react";
import { ChevronDown, FolderOpen, Info, X } from "lucide-react";
import BelegProjektFeld from "@/components/BelegProjektFeld";

/**
 * BelegProjektSchild – Projekt des Belegs als deutliches Schild in der festen oberen Leiste (A-015b, 17.10.2026).
 * Rutscht beim Scrollen nicht weg. Klick öffnet die Auswahlliste „Projekt“ (BelegProjektFeld).
 * „Aus dem Projekt angelegt“ bleibt änderbar, mit Hinweis.
 *
 * Props: kundeId, value, onChange, neu, fest, daten (aus useBelegProjekte), autoOffen, testId
 */
const BelegProjektSchild = ({ kundeId, value, onChange, neu, fest, daten, autoOffen = false, testId = "projekt-schild" }) => {
  const [offen, setOffen] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (autoOffen && kundeId) setOffen(true); }, [autoOffen, kundeId]);
  useEffect(() => {
    if (!offen) return undefined;
    const weg = (e) => { if (ref.current && !ref.current.contains(e.target)) setOffen(false); };
    document.addEventListener("mousedown", weg);
    return () => document.removeEventListener("mousedown", weg);
  }, [offen]);

  const projekte = daten?.projekte || [];
  const aktuell = value ? projekte.find((p) => p.id === value) : null;
  const offene = projekte.filter((p) => p.offen);

  // Farbe und Text: grün = Projekt gewählt, gelb = fehlt (Stolperstein), grau = erst Kunde wählen
  let farbe = "bg-amber-100 text-amber-900 border-amber-400 hover:bg-amber-200";
  let text;
  let zustand = "fehlt";
  if (!kundeId) { text = "Projekt: erst Kunde wählen"; farbe = "bg-slate-100 text-slate-600 border-slate-300"; zustand = "kein-kunde"; }
  else if (!daten) { text = "Projekt …"; farbe = "bg-slate-100 text-slate-600 border-slate-300"; zustand = "laedt"; }
  else if (aktuell) { text = aktuell.titel; farbe = "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700"; zustand = "gewaehlt"; }
  else if (value) { text = "Projekt nicht gefunden – bitte wählen"; }
  else if (offene.length > 0) { text = offene.length > 1 ? "Projekt wählen!" : "Ohne Projekt – zuordnen"; }
  else { text = "Ohne Projekt"; farbe = "bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200"; zustand = "ohne"; }

  const waehlen = (id) => { onChange?.(id); setOffen(false); };

  return (
    <div className="relative min-w-0" ref={ref} data-testid={testId} data-zustand={zustand}>
      <button type="button" onClick={() => setOffen((o) => !o)} disabled={!kundeId}
        title={aktuell ? `Projekt: ${aktuell.titel} – klicken zum Ändern` : "Projekt wählen"}
        className={`inline-flex items-center gap-1.5 max-w-full h-8 lg:h-9 px-2.5 rounded-sm border text-xs lg:text-sm font-semibold transition-colors ${farbe}`}
        data-testid={`${testId}-knopf`}>
        <FolderOpen className="w-4 h-4 shrink-0" />
        <span className="hidden 2xl:inline opacity-80 font-normal">Projekt:</span>
        <span className="truncate" data-testid={`${testId}-text`}>{text}</span>
        {kundeId && <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${offen ? "rotate-180" : ""}`} />}
      </button>
      {offen && (
        <div className="fixed left-2 right-2 top-24 sm:absolute sm:left-0 sm:right-auto sm:top-full sm:mt-1 sm:w-[24rem] z-50 bg-card text-card-foreground border rounded-sm shadow-lg p-3 space-y-2"
          data-testid={`${testId}-auswahl`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold">Zu welchem Projekt gehört dieser Beleg?</span>
            <button type="button" onClick={() => setOffen(false)} className="p-1 rounded-sm hover:bg-muted" aria-label="Schließen"><X className="w-4 h-4" /></button>
          </div>
          {fest && (
            <div className="flex items-start gap-1.5 text-[11px] text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-sm p-2" data-testid={`${testId}-fest-hinweis`}>
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Dieser Beleg wurde aus dem Projekt angelegt. Ändern Sie das Projekt nur, wenn er wirklich zu einem anderen Projekt gehört.</span>
            </div>
          )}
          <BelegProjektFeld kundeId={kundeId} value={value} onChange={waehlen} neu={neu} daten={daten} testId={`${testId}-feld`} />
          <div className="text-[11px] text-muted-foreground">Gespeichert wird mit „Speichern“.</div>
        </div>
      )}
    </div>
  );
};

export default BelegProjektSchild;
